import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { ReloadOutlined } from '@ant-design/icons';
import { Modal, Button, DatePicker, Space, Divider, Input } from 'antd';
import qs from 'qs';
import _ from 'lodash';
import { checkPermission } from '@/utils/permission';
import { cleanObject, isValidDate, toMoment } from '@/utils/helpers';

import {
    fetchCusServiceMilestones,
    updateCusServiceMilestone,
    notifyCusServiceMilestone
} from '@/redux/actions/cusServiceMilestoneActions';
import DynamicTable from '@/components/DynamicTable';
import moment from 'moment';
import BaseRangeFilter from '@/components/BaseRangeFilter';
import BaseSelect from '@/components/Elements/BaseSelect';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/milestones', breadcrumbName: 'Quản lý ca Invisalign' },
];

export default function MilestoneList() {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const items = useSelector(state => state.cusServiceMilestoneReducer.list.items);
    const pagination = useSelector(state => state.cusServiceMilestoneReducer.list.pagination);
    const doctors = useSelector(state => state.config.doctors);

    const [isLoading, setIsLoading] = useState(false);

    const routeName = 'milestones';
    const allowUpdate = checkPermission(routeName, 'update');
    const allowNotify = checkPermission(routeName, 'notify');

    const query = qs.parse(location.search.slice(1));

    const loadData = useCallback((q = query) => {
        setIsLoading(true);
        dispatch(fetchCusServiceMilestones(q)).finally(() => setIsLoading(false));
    }, [dispatch, query]);

    useEffect(() => {
        loadData();
    }, [location.search]);

    const formatDate = (d) => (moment.isMoment(d) && d.isValid()) ? d.format('YYYY-MM-DD') : undefined;

    const handleFilterChange = (name, value) => {
        let q = { ...query, [name]: value };

        const rangeFilters = {
            start_date: ['start_date_from', 'start_date_to'],
            created_at: ['created_at_from', 'created_at_to'],
        };

        if (rangeFilters[name]) {
            const [fromKey, toKey] = rangeFilters[name];
            const [start, end] = value || [];

            q[fromKey] = formatDate(start);
            q[toKey] = formatDate(end);

            // Nếu không có ngày hợp lệ, xóa cả hai
            if (!q[fromKey] && !q[toKey]) {
                delete q[fromKey];
                delete q[toKey];
            }

            delete q[name];
        }
        console.log(name, value);
        q = cleanObject(q);
        history.push({ pathname: location.pathname, search: qs.stringify(q) });
    };

    // Hàm update chung
    const handleUpdate = (id, field, value, oldValue) => {

        let formattedValue = null;
        if (moment.isMoment(value) && value.isValid()) {
            formattedValue = value.format("YYYY-MM-DD");
        } else if (!value) {
            formattedValue = null;
        } else {
            formattedValue = value
        }
        const newVal = String(formattedValue || '').trim();
        const oldVal = String(oldValue || '').trim();

        if (newVal === oldVal) return;

        console.log('formattedValue', formattedValue);
        dispatch(updateCusServiceMilestone(id, { [field]: formattedValue }))
            .catch(console.error);
    };

    // Hàm notify chung
    const handleNotify = async (id, field) => {
        try {
            // Map từng mốc sang title tương ứng
            const mapTitles = {
                has_clincheck_date: 'Có ClinCheck mới',
                submit_date: 'Đã submit hồ sơ điều trị',
                approve_date: 'Đã approve ClinCheck',
                payment_date: 'Đã thanh toán sản xuất khay',
                tray_arrival_date: 'Khay Invisalign đã về',
                scan_date: 'Đã scan / chụp phim mới',
            };

            const title = mapTitles[field] || 'Cập nhật mốc điều trị mới';

            // Payload gửi backend
            const body = {
                milestone_type: 'invisalign',
                title, // chỉ gửi title
            };

            await dispatch(notifyCusServiceMilestone(id, body));

            Modal.success({
                title: 'Đã gửi thông báo',
                content: `Thông báo: ${title}`,
            });
        } catch (error) {
            console.error('[handleNotify] Error:', error);
            Modal.error({
                title: 'Gửi thông báo thất bại',
                content: error?.response?.data?.msg || error.message || 'Vui lòng thử lại sau.',
            });
        }
    };

    const columns = [
        {
            title: 'Tên bệnh nhân',
            dataIndex: 'customer_name',
            key: 'customer_name',
            width: '250px',
            render: (_, record) => (
                <CustomerInfoColumn
                    record={record}
                    nameField="customer_name"          // field tuỳ module
                    idField="customer_id"
                    codeField="customer_code"
                    branchField="branch_names"
                    medicalBookField="medical_book_number"
                    conditionField="conditions"
                />
            ),
        },
        {
            title: 'Invisalign PID',
            dataIndex: 'invisalign_id',
            key: 'invisalign_id',
            width: 130,
            render: (_, record) => {
                return (
                    <Input
                        disabled={!allowUpdate}
                        defaultValue={record.invisalign_id}
                        name="invisalign_id"
                        onBlur={(e) =>
                            handleUpdate(record.id, e.target.name, e.target.value, record.invisalign_id)
                        }
                        onPressEnter={(e) =>
                            handleUpdate(record.id, e.target.name, e.target.value, record.invisalign_id)
                        }
                    />
                );
            },
        },
        {
            title: 'Bác sĩ phụ trách',
            dataIndex: ['doctor', 'full_name'],
            key: 'doctor',
            width: 180,
            render: (_, record) => record.doctors
        },
        {
            title: 'Ngày bắt đầu',
            dataIndex: 'start_date',
            key: 'start_date',
            width: 120,
            render: (val) => val ? isValidDate(val, false) : null,
        },
        {
            title: 'Ngày scan/chụp phim',
            dataIndex: 'scan_date',
            key: 'scan_date',
            width: 180,
            render: (val, record) => {
                return (
                    <DatePicker
                        allowClear
                        disabled={!allowUpdate}
                        value={toMoment(val)}
                        format={'DD/MM/YYYY'}
                        onChange={(date) => handleUpdate(record.id, 'scan_date', date, val)}
                    />
                );
            }
        },
        {
            title: 'Ngày submit',
            dataIndex: 'submit_date',
            key: 'submit_date',
            width: 160,
            align: 'center',
            render: (val, record) => {
                return (
                    <Space size="small" align="center" direction="vertical">
                        <DatePicker
                            allowClear
                            disabled={!allowUpdate}
                            value={toMoment(val)}
                            format={'DD/MM/YYYY'}
                            onChange={(date) => handleUpdate(record.id, 'submit_date', date, val)}
                        />

                        <Button disabled={!allowNotify} type="primary" size="small" onClick={() => handleNotify(record.id, 'submit_date')}>
                            Gửi thông báo
                        </Button>
                    </Space>
                );
            }
        },
        {
            title: 'Ngày có ClinCheck',
            dataIndex: 'has_clincheck_date',
            key: 'has_clincheck_date',
            width: 160,
            align: 'center',
            render: (val, record) => {
                return (
                    <Space size="small" align="center" direction="vertical">
                        <DatePicker
                            allowClear
                            disabled={!allowUpdate}
                            value={toMoment(val)}
                            format={'DD/MM/YYYY'}
                            onChange={(date) => handleUpdate(record.id, 'has_clincheck_date', date, val)}
                        />

                        <Button disabled={!allowNotify} type="primary" size="small" onClick={() => handleNotify(record.id, 'has_clincheck_date')}>
                            Gửi thông báo
                        </Button>
                    </Space>
                )
            }
        },
        {
            title: 'Ngày đóng tiền sx khay',
            dataIndex: 'payment_date',
            key: 'payment_date',
            width: 180,
            render: (val, record) => {
                return (
                    <DatePicker
                        allowClear
                        disabled={!allowUpdate}
                        value={toMoment(val)}
                        format={'DD/MM/YYYY'}
                        onChange={(date) => handleUpdate(record.id, 'payment_date', date, val)}
                    />
                )
            }
        },
        {
            title: 'Ngày approve',
            dataIndex: 'approve_date',
            key: 'approve_date',
            width: 160,
            align: 'center',
            render: (val, record) => {
                return (
                    <Space size="small" align="center" direction="vertical">
                        <DatePicker
                            allowClear
                            disabled={!allowUpdate}
                            value={toMoment(val)}
                            format={'DD/MM/YYYY'}
                            onChange={(date) => handleUpdate(record.id, 'approve_date', date, val)}
                        />

                        <Button disabled={!allowNotify} type="primary" size="small" onClick={() => handleNotify(record.id, 'approve_date')}>
                            Gửi thông báo
                        </Button>
                    </Space>
                )
            }
        },
        {
            title: 'Ngày khay về',
            dataIndex: 'tray_arrival_date',
            key: 'tray_arrival_date',
            width: 140,
            render: (val, record) => {
                return (
                    <Space size="small" align="center" direction="vertical">
                        <DatePicker
                            allowClear
                            disabled={!allowUpdate}
                            value={toMoment(val)}
                            format={'DD/MM/YYYY'}
                            onChange={(date) => handleUpdate(record.id, 'tray_arrival_date', date, val)}
                        />
                        <Button disabled={!allowNotify} type="primary" size="small" onClick={() => handleNotify(record.id, 'tray_arrival_date')}>
                            Gửi thông báo
                        </Button>
                    </Space>
                )
            }
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'birthday',
            key: 'birthday',
            width: 120,
            render: (text, record) => {
                if (!text) return <span style={{ color: '#bfbfbf' }}>—</span>;

                const birthDate = moment(text);
                const isValid = birthDate.isValid();
                if (!isValid) return <span style={{ color: '#bfbfbf' }}>—</span>;

                const age = record.customer_age;
                const formattedDate = isValidDate(record.birthday, false);

                return (
                    <div style={{ lineHeight: 1.3 }}>
                        <div style={{ fontWeight: 500, color: '#222' }}>
                            {formattedDate}
                        </div>
                        {age && (
                            <div style={{ fontSize: 14, color: '#8c8c8c', marginTop: 2 }}>
                                {age} tuổi
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Địa điểm',
            dataIndex: 'location',
            key: 'location',
            width: 120,
            render: (val, record) => {
                return (
                    <>{record.customer_service?.customer?.branche.name}</>
                )
            }
        },
        {
            title: 'Gói chỉnh nha',
            dataIndex: ['service', 'name'],
            key: 'customer_service',
            width: 220,
            render: (val, record) => {
                return (
                    <>{record.customer_service?.service?.name}</>
                )
            }
        }
    ];

    return (
        <div>
            <PageTitle routes={routes} title="Quản lý ca Invisalign" />
            <TableActionBar
                isShowAddButton={false}
                isShowDeleteButton={false}
                showSearch
                showFilter={false}
                activeFilter={query.status}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
                placeholderSearch="Tên bn, mã bn, sdt, invi ID"
            >
                <BaseRangeFilter
                    name="start_date"
                    query={query}
                    onChangeFilter={handleFilterChange}
                    placeholder={['Ngày bắt dầu', 'Đến ngày']}
                    width="300px"
                />
                <Divider type="vertical" />
                <BaseSelect
                    options={doctors || []}
                    optionValue="user_id"
                    optionLabel="display_name"
                    showSearch
                    placeholder="Bác sĩ thực hiện"
                    style={{ width: 200 }}
                    onChange={(value) => handleFilterChange('doctor_id', value)}
                    defaultValue={query.doctor_id ? parseInt(query.doctor_id) : null}
                />
                <Divider type="vertical" />
                <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData()}>Refresh</Button>
            </TableActionBar>

            <div className='has-custom-column-table-btn mt-3'>
                <DynamicTable
                    dataSource={items || []}
                    columns={columns}
                    isLoading={isLoading}
                    pagination={pagination}
                    rowSelection={false}
                    moduleKey="milestone_list"
                />
            </div>
        </div>
    );
}